import React, { useState, useEffect, Activity } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { SearchableSelect, type SearchableSelectOption } from "../components/ui/searchable-select";
import { Trash2, Plus, Edit, Settings, Users, Package } from "lucide-react";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { formatThaiDate } from "@/shared/lib/rfm-translations";
import { t } from "@/shared/lib/th-translations";

interface DefaultRate {
  id: number;
  sku_prefix: string;
  commission_rate: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface CustomRate {
  id: number;
  salesperson_id: string;
  sku_prefix: string;
  commission_rate: number;
  created_at: string;
  updated_at: string;
  employeeNumber?: string | null;
  salespersonName?: string | null;
}

interface RateStatistics {
  total_default_rates: number;
  total_custom_rates: number;
  avg_default_rate: number;
  avg_custom_rate: number;
}

interface Salesperson {
  gUId: string;
  employeeNumber: string;
  displayName: string;
}

export default function AdminCommissionRates() {
  const api = useAuthenticatedApi();
  const [defaultRates, setDefaultRates] = useState<DefaultRate[]>([]);
  const [customRates, setCustomRates] = useState<CustomRate[]>([]);
  const [statistics, setStatistics] = useState<RateStatistics | null>(null);
  const [salespersons, setSalespersons] = useState<Salesperson[]>([]);
  const [loadingSalespersons, setLoadingSalespersons] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('default-rates');

  // Form states
  const [isDefaultRateDialogOpen, setIsDefaultRateDialogOpen] = useState(false);
  const [isCustomRateDialogOpen, setIsCustomRateDialogOpen] = useState(false);
  const [editingDefaultRate, setEditingDefaultRate] = useState<DefaultRate | null>(null);
  const [editingCustomRate, setEditingCustomRate] = useState<CustomRate | null>(null);

  const [defaultRateForm, setDefaultRateForm] = useState({
    sku_prefix: "",
    commission_rate: "",
    description: ""
  });

  const [customRateForm, setCustomRateForm] = useState({
    salesperson_id: "",
    sku_prefix: "",
    commission_rate: ""
  });

  // Fetch data
  useEffect(() => {
    fetchDefaultRates();
    fetchCustomRates();
    fetchSalespersons();
  }, []);

  const fetchDefaultRates = async () => {
    try {
      const response = await api.request("/api/admin/commission-rates/default-rates");
      const data: any = await response.json();
      if (data.success) {
        setDefaultRates(data.data.rates || []);
        // Store default stats to merge with custom stats later
        setStatistics((prev) => ({
          ...prev,
          total_default_rates: data.data.stats.totalRates || 0,
          avg_default_rate: data.data.stats.averageRate || 0,
        } as any));
      } else {
        setError(data.error || "Failed to fetch default rates");
      }
    } catch (err) {
      setError("Error fetching default rates");
      console.error(err);
    }
  };

  const fetchCustomRates = async () => {
    try {
      const response = await api.request("/api/admin/commission-rates/custom-rates?limit=100");
      const data: any = await response.json();
      if (data.success) {
        setCustomRates(data.data.rates || []);
        // Merge custom stats with default stats
        setStatistics((prev) => ({
          ...prev,
          total_custom_rates: data.data.stats?.totalCustomRates || 0,
          avg_custom_rate: data.data.stats?.averageCustomRate || 0,
        } as any));
      } else {
        setError(data.error || "Failed to fetch custom rates");
      }
    } catch (err) {
      setError("Error fetching custom rates");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalespersons = async () => {
    setLoadingSalespersons(true);
    try {
      const response = await api.request("/api/admin/commission-rates/salespersons");
      const data: any = await response.json();
      if (data.success) {
        setSalespersons(data.data.salespersons || []);
      } else {
        console.error("Failed to fetch salespersons:", data.error);
      }
    } catch (err) {
      console.error("Error fetching salespersons:", err);
    } finally {
      setLoadingSalespersons(false);
    }
  };

  // Default rate handlers
  const handleCreateDefaultRate = async () => {
    try {
      const response = await api.request("/api/admin/commission-rates/default-rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sku_prefix: defaultRateForm.sku_prefix.toUpperCase(),
          commission_rate: parseFloat(defaultRateForm.commission_rate) / 100,
          description: defaultRateForm.description || undefined
        })
      });

      const data: any = await response.json();
      if (data.success) {
        await fetchDefaultRates();
        setIsDefaultRateDialogOpen(false);
        setDefaultRateForm({ sku_prefix: "", commission_rate: "", description: "" });
        setEditingDefaultRate(null);
      } else {
        setError(data.error || "Failed to save default rate");
      }
    } catch (err) {
      setError("Error saving default rate");
      console.error(err);
    }
  };

  const handleDeleteDefaultRate = async (skuPrefix: string) => {
    if (!confirm(`Are you sure you want to delete the default rate for ${skuPrefix}?`)) return;

    try {
      const response = await api.request(`/api/admin/commission-rates/default-rates/${skuPrefix}`, {
        method: "DELETE"
      });

      const data: any = await response.json();
      if (data.success) {
        await fetchDefaultRates();
      } else {
        setError(data.error || "Failed to delete default rate");
      }
    } catch (err) {
      setError("Error deleting default rate");
      console.error(err);
    }
  };

  // Custom rate handlers
  const handleCreateCustomRate = async () => {
    try {
      const response = await api.request("/api/admin/commission-rates/custom-rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          salesperson_id: customRateForm.salesperson_id,
          sku_prefix: customRateForm.sku_prefix.toUpperCase(),
          commission_rate: parseFloat(customRateForm.commission_rate) / 100
        })
      });

      const data: any = await response.json();
      if (data.success) {
        await fetchCustomRates();
        setIsCustomRateDialogOpen(false);
        setCustomRateForm({ salesperson_id: "", sku_prefix: "", commission_rate: "" });
        setEditingCustomRate(null);
      } else {
        setError(data.error || "Failed to save custom rate");
      }
    } catch (err) {
      setError("Error saving custom rate");
      console.error(err);
    }
  };

  const handleDeleteCustomRate = async (salespersonId: string, skuPrefix: string) => {
    if (!confirm(`Are you sure you want to delete the custom rate for ${salespersonId} - ${skuPrefix}?`)) return;

    try {
      const response = await api.request(`/api/admin/commission-rates/custom-rates/${salespersonId}/${skuPrefix}`, {
        method: "DELETE"
      });

      const data: any = await response.json();
      if (data.success) {
        await fetchCustomRates();
      } else {
        setError(data.error || "Failed to delete custom rate");
      }
    } catch (err) {
      setError("Error deleting custom rate");
      console.error(err);
    }
  };

  const openEditDefaultRate = (rate: DefaultRate) => {
    setEditingDefaultRate(rate);
    setDefaultRateForm({
      sku_prefix: rate.sku_prefix,
      commission_rate: (rate.commission_rate * 100).toString(),
      description: rate.description || ""
    });
    setIsDefaultRateDialogOpen(true);
  };

  const openEditCustomRate = (rate: CustomRate) => {
    setEditingCustomRate(rate);
    setCustomRateForm({
      salesperson_id: rate.salesperson_id,
      sku_prefix: rate.sku_prefix,
      commission_rate: (rate.commission_rate * 100).toString()
    });
    setIsCustomRateDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold">{t("adminRates.title")}</h1>
            <p className="text-muted-foreground">
              {t("adminRates.subtitle")}
            </p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm">
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <p className="text-red-600">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setError(null)}
              >
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Default Rates</p>
                    <p className="text-2xl font-bold">{statistics.total_default_rates}</p>
                  </div>
                  <Settings className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Custom Rates</p>
                    <p className="text-2xl font-bold">{statistics.total_custom_rates}</p>
                  </div>
                  <Users className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Avg Default Rate</p>
                    <p className="text-2xl font-bold">{(statistics.avg_default_rate * 100).toFixed(1)}%</p>
                  </div>
                  <Package className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Avg Custom Rate</p>
                    <p className="text-2xl font-bold">{(statistics.avg_custom_rate * 100).toFixed(1)}%</p>
                  </div>
                  <Settings className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="default-rates" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Default Rates
            </TabsTrigger>
            <TabsTrigger value="custom-rates" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Custom Rates
            </TabsTrigger>
          </TabsList>

          {/* Default Rates Tab */}
          <TabsContent value="default-rates" className="space-y-6">
            <Activity mode={activeTab === 'default-rates' ? 'visible' : 'hidden'}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Default Commission Rates</CardTitle>
                  <Dialog open={isDefaultRateDialogOpen} onOpenChange={setIsDefaultRateDialogOpen}>
                    <DialogTrigger>
                      <Button
                        onClick={() => {
                          setEditingDefaultRate(null);
                          setDefaultRateForm({ sku_prefix: "", commission_rate: "", description: "" });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t("adminRates.addDefaultRate")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingDefaultRate ? t("adminRates.editDefaultRate") : t("adminRates.addDefaultRateTitle")}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="sku_prefix">{t("adminRates.skuPrefix")}</Label>
                          <Input
                            id="sku_prefix"
                            value={defaultRateForm.sku_prefix}
                            onChange={(e) => setDefaultRateForm(prev => ({ ...prev, sku_prefix: e.target.value }))}
                            placeholder={t("adminRates.skuPrefixPlaceholder")}
                            disabled={!!editingDefaultRate}
                          />
                        </div>
                        <div>
                          <Label htmlFor="commission_rate">{t("adminRates.commissionRate")}</Label>
                          <Input
                            id="commission_rate"
                            type="number"
                            step="0.1"
                            value={defaultRateForm.commission_rate}
                            onChange={(e) => setDefaultRateForm(prev => ({ ...prev, commission_rate: e.target.value }))}
                            placeholder={t("adminRates.ratePlaceholder")}
                          />
                        </div>
                        <div>
                          <Label htmlFor="description">{t("adminRates.description")}</Label>
                          <Textarea
                            id="description"
                            value={defaultRateForm.description}
                            onChange={(e) => setDefaultRateForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder={t("adminRates.descriptionPlaceholder")}
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            onClick={() => setIsDefaultRateDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button onClick={handleCreateDefaultRate}>
                            {editingDefaultRate ? "Update" : "Create"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {defaultRates.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">{t("adminRates.noDefaultRates")}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU Prefix</TableHead>
                        <TableHead>Commission Rate</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {defaultRates.map((rate) => (
                        <TableRow key={rate.id}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {rate.sku_prefix}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {(rate.commission_rate * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {rate.description || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatThaiDate(rate.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDefaultRate(rate)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteDefaultRate(rate.sku_prefix)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            </Activity>
          </TabsContent>

          {/* Custom Rates Tab */}
          <TabsContent value="custom-rates" className="space-y-6">
            <Activity mode={activeTab === 'custom-rates' ? 'visible' : 'hidden'}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Custom Commission Rates</CardTitle>
                  <Dialog open={isCustomRateDialogOpen} onOpenChange={setIsCustomRateDialogOpen}>
                    <DialogTrigger>
                      <Button
                        onClick={() => {
                          setEditingCustomRate(null);
                          setCustomRateForm({ salesperson_id: "", sku_prefix: "", commission_rate: "" });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t("adminRates.addCustomRate")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingCustomRate ? t("adminRates.editCustomRate") : t("adminRates.addCustomRateTitle")}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="salesperson_id">{t("adminRates.salesperson")}</Label>
                          {loadingSalespersons ? (
                            <div className="flex items-center justify-center h-9 border rounded-md">
                              <span className="text-sm text-muted-foreground">Loading...</span>
                            </div>
                          ) : (
                            <SearchableSelect
                              options={salespersons.map((s): SearchableSelectOption => ({
                                value: s.gUId,
                                label: s.employeeNumber
                              }))}
                              value={customRateForm.salesperson_id}
                              onValueChange={(value) => setCustomRateForm(prev => ({ ...prev, salesperson_id: value }))}
                              placeholder={t("adminRates.selectSalesperson")}
                              searchPlaceholder={t("adminRates.searchSalesperson")}
                              emptyText={t("adminRates.noSalespersonFound")}
                              disabled={!!editingCustomRate}
                            />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="custom_sku_prefix">{t("adminRates.skuPrefix")}</Label>
                          <SearchableSelect
                            options={defaultRates.map((rate): SearchableSelectOption => ({
                              value: rate.sku_prefix,
                              label: `${rate.sku_prefix} - ${rate.description || "No description"} (${(rate.commission_rate * 100).toFixed(1)}%)`
                            }))}
                            value={customRateForm.sku_prefix}
                            onValueChange={(value) => setCustomRateForm(prev => ({ ...prev, sku_prefix: value }))}
                            placeholder={t("adminRates.selectSkuPrefix")}
                            searchPlaceholder={t("adminRates.searchSkuPrefix")}
                            emptyText={t("adminRates.noSkuPrefixFound")}
                            disabled={!!editingCustomRate}
                          />
                        </div>
                        <div>
                          <Label htmlFor="custom_commission_rate">{t("adminRates.commissionRate")}</Label>
                          <Input
                            id="custom_commission_rate"
                            type="number"
                            step="0.1"
                            value={customRateForm.commission_rate}
                            onChange={(e) => setCustomRateForm(prev => ({ ...prev, commission_rate: e.target.value }))}
                            placeholder={t("adminRates.ratePlaceholder")}
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            onClick={() => setIsCustomRateDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button onClick={handleCreateCustomRate}>
                            {editingCustomRate ? "Update" : "Create"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {customRates.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">{t("adminRates.noCustomRates")}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Salesperson</TableHead>
                        <TableHead>SKU Prefix</TableHead>
                        <TableHead>Commission Rate</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customRates.map((rate) => (
                        <TableRow key={`${rate.salesperson_id}-${rate.sku_prefix}`}>
                          <TableCell className="font-mono text-sm">
                            {rate.employeeNumber || rate.salespersonName || rate.salesperson_id}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {rate.sku_prefix}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {(rate.commission_rate * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatThaiDate(rate.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditCustomRate(rate)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCustomRate(rate.salesperson_id, rate.sku_prefix)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            </Activity>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}